import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  ApplyManualOverrideRequest,
  CancelTimetableJobResponse,
  CatalogueReadinessView,
  QuickGenerateRequest,
  TimetableDetailResponse,
  TimetableJobListResponse,
  TimetableJobView,
  TimetableListResponse,
  TimetableSlotView,
  TimetableView,
} from "@eduviaos/shared";
import { api } from "@/lib/api";

/**
 * Phase G3 — IA timetable hooks.
 *
 * Backed by the BE-2c admin/scheduling routes:
 *   GET    /admin/scheduling/timetable-jobs
 *   GET    /admin/scheduling/timetable-jobs/{id}
 *   POST   /admin/scheduling/timetable-jobs/{id}:cancel
 *   GET    /admin/scheduling/timetables
 *   GET    /admin/scheduling/timetables/{id}
 *   PATCH  /admin/scheduling/timetables/{id}/slots/{slot_id}
 *   POST   /admin/scheduling/timetables/{id}:publish
 *   POST   /admin/scheduling/timetables/{id}:archive
 *
 * Stale window: 15s for the lists (the IA dashboard polls solver
 * progress) and 60s for individual records — slot edits / publish /
 * archive bust the relevant cache via `invalidateQueries` on success.
 *
 * The submit-new-job flow lands in G3 FE-1d (constraint sheet). The
 * teacher-view filter lands in G3 FE-1e. This file ships the shapes
 * for both today (the QueryClient cache keys are forward-compat).
 */

const TIMETABLE_ROOT = "/admin/scheduling";

// ── Query keys ───────────────────────────────────────────────────────

export const timetableKeys = {
  all: ["timetable"] as const,
  jobs: () => [...timetableKeys.all, "jobs"] as const,
  jobsList: (filters: TimetableJobFilters) => [...timetableKeys.jobs(), "list", filters] as const,
  job: (jobId: string) => [...timetableKeys.jobs(), "detail", jobId] as const,
  timetables: () => [...timetableKeys.all, "timetables"] as const,
  timetablesList: (filters: TimetableListFilters) =>
    [...timetableKeys.timetables(), "list", filters] as const,
  timetable: (timetableId: string) =>
    [...timetableKeys.timetables(), "detail", timetableId] as const,
  catalogueReadiness: (filters: CatalogueReadinessFilters) =>
    [...timetableKeys.all, "readiness", filters] as const,
};

// ── Filters ──────────────────────────────────────────────────────────

export interface TimetableJobFilters {
  branchId?: string | null;
  academicYearId?: string | null;
  /** Optional status filter — repeatable on the BE. */
  statuses?: string[];
  limit?: number;
  offset?: number;
}

export interface TimetableListFilters {
  branchId?: string | null;
  academicYearId?: string | null;
  statuses?: string[];
  limit?: number;
  offset?: number;
}

export interface CatalogueReadinessFilters {
  branchId: string;
  academicYearId: string;
}

function buildJobsQuery(filters: TimetableJobFilters): string {
  const params = new URLSearchParams();
  if (filters.branchId) params.set("branch_id", filters.branchId);
  if (filters.academicYearId) params.set("academic_year_id", filters.academicYearId);
  for (const status of filters.statuses ?? []) {
    params.append("status", status);
  }
  if (filters.limit !== undefined) params.set("limit", String(filters.limit));
  if (filters.offset !== undefined) params.set("offset", String(filters.offset));
  return params.toString();
}

function buildTimetablesQuery(filters: TimetableListFilters): string {
  const params = new URLSearchParams();
  if (filters.branchId) params.set("branch_id", filters.branchId);
  if (filters.academicYearId) params.set("academic_year_id", filters.academicYearId);
  for (const status of filters.statuses ?? []) {
    params.append("status", status);
  }
  if (filters.limit !== undefined) params.set("limit", String(filters.limit));
  if (filters.offset !== undefined) params.set("offset", String(filters.offset));
  return params.toString();
}

// ── Reads ────────────────────────────────────────────────────────────

export function useTimetableJobs(filters: TimetableJobFilters = {}) {
  return useQuery<TimetableJobListResponse>({
    queryKey: timetableKeys.jobsList(filters),
    queryFn: () => {
      const query = buildJobsQuery(filters);
      const url = query
        ? `${TIMETABLE_ROOT}/timetable-jobs?${query}`
        : `${TIMETABLE_ROOT}/timetable-jobs`;
      return api.get<TimetableJobListResponse>(url);
    },
    // Solver progress polling — 15s is short enough to feel live
    // without hammering the BE. The detail hook polls faster.
    staleTime: 15_000,
  });
}

export function useTimetableJob(jobId: string | null | undefined) {
  return useQuery<TimetableJobView>({
    queryKey: timetableKeys.job(jobId ?? ""),
    enabled: Boolean(jobId),
    queryFn: () =>
      api.get<TimetableJobView>(
        `${TIMETABLE_ROOT}/timetable-jobs/${encodeURIComponent(jobId ?? "")}`,
      ),
    // While a job is running we want the FE to feel live. The
    // page-level hook can flip `refetchInterval` based on status.
    staleTime: 5_000,
  });
}

export function useTimetables(filters: TimetableListFilters = {}) {
  return useQuery<TimetableListResponse>({
    queryKey: timetableKeys.timetablesList(filters),
    queryFn: () => {
      const query = buildTimetablesQuery(filters);
      const url = query ? `${TIMETABLE_ROOT}/timetables?${query}` : `${TIMETABLE_ROOT}/timetables`;
      return api.get<TimetableListResponse>(url);
    },
    staleTime: 60_000,
  });
}

export function useTimetableDetail(timetableId: string | null | undefined) {
  return useQuery<TimetableDetailResponse>({
    queryKey: timetableKeys.timetable(timetableId ?? ""),
    enabled: Boolean(timetableId),
    queryFn: () =>
      api.get<TimetableDetailResponse>(
        `${TIMETABLE_ROOT}/timetables/${encodeURIComponent(timetableId ?? "")}`,
      ),
    staleTime: 30_000,
  });
}

/**
 * Phase G3.6 BE-1 — pre-flight readiness for the quick-generate
 * flow. The IA dashboard reads this BEFORE submitting a job so it
 * can either gate the CTA with a banner listing missing pieces
 * (subjects / sections / teacher eligibility / grade demand) or
 * let the admin proceed.
 *
 * The hook is enabled only when both `branchId` and
 * `academicYearId` are supplied — the BE rejects empty values
 * with a 400 and the FE has no useful answer to render until both
 * are typed in. Stale window is 30s (the catalogue rarely changes
 * mid-session, but admins can return after onboarding teachers).
 */
export function useCatalogueReadiness(filters: CatalogueReadinessFilters) {
  const { branchId, academicYearId } = filters;
  return useQuery<CatalogueReadinessView>({
    queryKey: timetableKeys.catalogueReadiness(filters),
    enabled: Boolean(branchId.trim() && academicYearId.trim()),
    queryFn: () => {
      const params = new URLSearchParams();
      params.set("branch_id", branchId);
      params.set("academic_year_id", academicYearId);
      return api.get<CatalogueReadinessView>(
        `${TIMETABLE_ROOT}/catalogue-readiness?${params.toString()}`,
      );
    },
    staleTime: 30_000,
  });
}

// ── Mutations ────────────────────────────────────────────────────────

export function useQuickGenerateTimetableJob() {
  const queryClient = useQueryClient();
  return useMutation<TimetableJobView, Error, QuickGenerateRequest>({
    mutationFn: (payload) =>
      api.post<TimetableJobView>(`${TIMETABLE_ROOT}/timetable-jobs:from-catalogue`, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: timetableKeys.jobs() });
    },
  });
}

export function useCancelTimetableJob() {
  const queryClient = useQueryClient();
  return useMutation<CancelTimetableJobResponse, Error, { jobId: string }>({
    mutationFn: ({ jobId }) =>
      api.post<CancelTimetableJobResponse>(
        `${TIMETABLE_ROOT}/timetable-jobs/${encodeURIComponent(jobId)}:cancel`,
        {},
      ),
    onSuccess: (_data, { jobId }) => {
      void queryClient.invalidateQueries({ queryKey: timetableKeys.jobs() });
      void queryClient.invalidateQueries({
        queryKey: timetableKeys.job(jobId),
      });
    },
  });
}

export function useApplyManualOverride() {
  const queryClient = useQueryClient();
  return useMutation<
    TimetableSlotView,
    Error,
    {
      timetableId: string;
      slotId: string;
      payload: ApplyManualOverrideRequest;
    }
  >({
    mutationFn: ({ timetableId, slotId, payload }) =>
      api.patch<TimetableSlotView>(
        `${TIMETABLE_ROOT}/timetables/${encodeURIComponent(
          timetableId,
        )}/slots/${encodeURIComponent(slotId)}`,
        payload,
      ),
    onSuccess: (_data, { timetableId }) => {
      void queryClient.invalidateQueries({
        queryKey: timetableKeys.timetable(timetableId),
      });
    },
  });
}

export function usePublishTimetable() {
  const queryClient = useQueryClient();
  return useMutation<TimetableView, Error, { timetableId: string }>({
    mutationFn: ({ timetableId }) =>
      api.post<TimetableView>(
        `${TIMETABLE_ROOT}/timetables/${encodeURIComponent(timetableId)}:publish`,
        {},
      ),
    onSuccess: (_data, { timetableId }) => {
      void queryClient.invalidateQueries({
        queryKey: timetableKeys.timetables(),
      });
      void queryClient.invalidateQueries({
        queryKey: timetableKeys.timetable(timetableId),
      });
    },
  });
}

export function useArchiveTimetable() {
  const queryClient = useQueryClient();
  return useMutation<TimetableView, Error, { timetableId: string }>({
    mutationFn: ({ timetableId }) =>
      api.post<TimetableView>(
        `${TIMETABLE_ROOT}/timetables/${encodeURIComponent(timetableId)}:archive`,
        {},
      ),
    onSuccess: (_data, { timetableId }) => {
      void queryClient.invalidateQueries({
        queryKey: timetableKeys.timetables(),
      });
      void queryClient.invalidateQueries({
        queryKey: timetableKeys.timetable(timetableId),
      });
    },
  });
}
